import { AfterViewInit, Directive, ElementRef, OnDestroy, Optional, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipListbox } from '@angular/material/chips';
import { MatRadioButton } from '@angular/material/radio';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Subject, takeUntil } from 'rxjs';
import { OrderState } from '../state/order.state';

/**
 * appReadOnly Directive
 *
 * Provides application-level read-only behavior.
 * - Disables Reactive / Template-driven form controls via NgControl.
 * - Disables native elements that support the `disabled` property.
 * - Supports Angular Material and custom components when integrated.
 * Designed for centralized interaction restriction (e.g. view mode).
 */

@Directive({
  selector: '[appReadOnly]',
  standalone: true,
})
export class ReadOnlyDirective implements AfterViewInit, OnDestroy {
  onDestroyNotifier$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private orderState: OrderState,
    @Optional() private ngControl: NgControl,
    @Optional() private matCheckbox: MatCheckbox,
    @Optional() private matRadioButton: MatRadioButton,
    @Optional() private matSlideToggle: MatSlideToggle,
    @Optional() private matChipListbox: MatChipListbox
  ) {}

  ngAfterViewInit() {
    this.orderState.readOnlyMode$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((isReadOnly) => {
      queueMicrotask(() => {
        const control = this.ngControl?.control;

        if (isReadOnly) {
          if (!control) {
            if (this.matCheckbox) this.matCheckbox.disabled = true;
            else if (this.matRadioButton) this.matRadioButton.disabled = true;
            else if (this.matSlideToggle) this.matSlideToggle.disabled = true;
            else if (this.matChipListbox) this.matChipListbox.disabled = true;
            else if (this.el.nativeElement) this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
          } else control.disable({ emitEvent: true });
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }
}
